import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import { serverEvents, debounce } from '../lib/events.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';
import type { ServerEvent } from '../lib/events.js';
import type {
  CompanyType, CompanyDetail, CompanyListRow, AccountRow, HistoryRow, SupplyRow,
  QuotationRow, InvoiceRow, SummaryRow,
} from '../lib/api.js';

type Mode = 'list' | 'detail' | 'create';
type Tab = 'info' | 'accounts' | 'account-detail' | 'supply' | 'history' | 'invoices' | 'items' | 'summary';

const TYPE_META: Record<CompanyType, { singular: string; title: string; icon: string; supplier: boolean }> = {
  opscustomer: { singular: 'Customer', title: 'Customers', icon: '🧑', supplier: false },
  opscompany: { singular: 'Supplier', title: 'Suppliers', icon: '🏭', supplier: true },
  opssalesrep: { singular: 'Sales Rep', title: 'Sales Reps', icon: '💼', supplier: false },
  opspromoter: { singular: 'Promoter', title: 'Promoters', icon: '📣', supplier: false },
};

/** Everything kept per outlet+type so flipping back re-draws the exact screen
 *  (search text, results, open record, active tab, loaded tab data). */
interface KeptCompanyView {
  mode: Mode;
  search: string;
  items: CompanyListRow[];
  error: string;
  notice: string;
  detail: CompanyDetail | null;
  draft: Partial<CompanyDetail> | null;
  dirty: boolean;
  tab: Tab;
  tabError: string;
  loadedTabs: Record<string, boolean>;
  accounts: AccountRow[];
  accountsDetail: AccountRow[];
  supply: SupplyRow[];
  history: HistoryRow[];
  invoices: InvoiceRow[];
  itemsList: QuotationRow[];
  summary: SummaryRow[];
  staleReloadId: string | null;
}

/**
 * Back-office company management panel — shared by Customers, Suppliers,
 * Sales Reps and Promoters (company_type ops*). List → Edit/View → detail
 * with the desktop CompanyEditPanel tabs: Info | Accounts | Accounts Detail |
 * Supply List (suppliers) | Purchase/Sales History | Invoices | Item List |
 * Transaction Summary.
 */
@customElement('company-manage')
export class CompanyManage extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }

    /* Detail tabs + list views: the header row stays fixed — only the data list scrolls. */
    .tab-body { flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .tab-body > .grid { flex: 1; min-height: 0; overflow-y: auto; align-content: start; }
    .tab-body > .table-wrap { flex: 1; min-height: 0; overflow: auto; }
    .tab-body > .table-wrap thead th { position: sticky; top: 0; z-index: 1; background: var(--color-surface); border-bottom: none; box-shadow: inset 0 -1px 0 var(--color-border); }

    .list-tools { display: flex; gap: 0.5rem; margin-bottom: 0.75rem; align-items: center; flex-shrink: 0; }
    .list-tools input { flex: 1; }

    .table-wrap { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 14px; overflow-x: auto; }
    th, td { white-space: nowrap; }
    td.wrap { white-space: normal; }

    .fg { min-width: 0; }

    .debit { color: var(--color-error, #d93025); }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: String }) companyType: CompanyType = 'opscompany';
  @property({ type: Boolean }) isAdmin = false;

  @state() private _mode: Mode = 'list';
  @state() private _search = '';
  @state() private _items: CompanyListRow[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _notice = '';

  @state() private _detail: CompanyDetail | null = null;
  @state() private _draft: Partial<CompanyDetail> | null = null;
  @state() private _dirty = false;
  @state() private _saving = false;

  @state() private _tab: Tab = 'info';
  @state() private _tabLoading = false;
  @state() private _tabError = '';
  @state() private _loadedTabs: Record<string, boolean> = {};
  @state() private _accounts: AccountRow[] = [];
  @state() private _accountsDetail: AccountRow[] = [];
  @state() private _supply: SupplyRow[] = [];
  @state() private _history: HistoryRow[] = [];
  @state() private _invoices: InvoiceRow[] = [];
  @state() private _itemsList: QuotationRow[] = [];
  @state() private _summary: SummaryRow[] = [];

  private get _meta() {
    return TYPE_META[this.companyType] || TYPE_META.opscompany;
  }

  private get _tabs(): Array<{ id: Tab; label: string }> {
    const supplier = this._meta.supplier;
    // Desktop parity (CompanyEditPanel.cpp): the tab strip is identical for all
    // four company windows — only the History flavour depends on the window's
    // opscode (supplier "opscompany" → Purchase History, everyone else →
    // Sales History). The Supply List tab is always present.
    return [
      { id: 'info', label: 'ℹ️ Info' },
      { id: 'accounts', label: '📒 Accounts' },
      { id: 'account-detail', label: '📑 Accounts Detail' },
      { id: 'supply', label: '📦 Supply List' },
      { id: 'history', label: supplier ? '🚚 Purchase History' : '🧾 Sales History' },
      { id: 'invoices', label: '💷 Invoices' },
      { id: 'items', label: '📋 Item List(History)' },
      { id: 'summary', label: '📊 Transaction Summary' },
    ];
  }

  /** Kept per outlet+type view — flipping to another outlet (or company tree
   *  node) saves this screen and re-draws it when the user flips back. */
  private _view = new OutletViewCache<KeptCompanyView>(this, 'company-manage', {
    capture: () => this._captureView(),
    restore: (v) => this._restoreView(v),
    reset: () => this._resetView(),
  });

  // Live server events (auto-refresh)
  @state() private _staleReloadId: string | null = null;
  private _eventUnsubs: Array<() => void> = [];
  private _reloadDetailSoon = debounce((id: string) => void this._refreshDetailQuiet(id), 700);
  private _reloadListSoon = debounce(() => {
    if (this.isConnected && this._mode === 'list') void this._loadList();
  }, 700);

  connectedCallback() {
    super.connectedCallback();
    this._eventUnsubs = [
      serverEvents.on('customer', this._onServerEvent),
      serverEvents.on('company', this._onServerEvent),
      serverEvents.on('salesrep', this._onServerEvent),
      serverEvents.on('promoter', this._onServerEvent),
      serverEvents.onLifecycle(this._onServerLifecycle),
    ];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._reloadDetailSoon.cancel();
    this._reloadListSoon.cancel();
    this._eventUnsubs.forEach((fn) => fn());
    this._eventUnsubs = [];
    // Keep the screen the user is leaving (also covers panel-type switches)
    // before this instance is discarded.
    this._view.dispose();
  }

  /**
   * The shell keeps THIS element mounted when switching between the four company
   * tree nodes (Customers / Suppliers / Sales Rep / Promoter) and when moving to
   * the same subtree of another outlet — property bindings never fire
   * attributeChangedCallback, so detect the change here. Each outlet+type pair
   * keeps its own screen (search, results, open record and tab) and restores it
   * when the user flips back; a first visit loads the list fresh.
   */
  protected updated() {
    const result = this._view.switchTo(this.storeCode ? `${this.storeCode}|${this.companyType}` : '');
    if (result === 'unchanged') return;
    // Drop refreshes queued for the outlet the user just left.
    this._reloadDetailSoon.cancel();
    this._reloadListSoon.cancel();
    if (result === 'fresh') void this._loadList();
  }

  private _captureView(): KeptCompanyView {
    return {
      mode: this._mode,
      search: this._search,
      items: this._items,
      error: this._error,
      notice: this._notice,
      detail: this._detail,
      draft: this._draft,
      dirty: this._dirty,
      tab: this._tab,
      tabError: this._tabError,
      loadedTabs: this._loadedTabs,
      accounts: this._accounts,
      accountsDetail: this._accountsDetail,
      supply: this._supply,
      history: this._history,
      invoices: this._invoices,
      itemsList: this._itemsList,
      summary: this._summary,
      staleReloadId: this._staleReloadId,
    };
  }

  private _restoreView(v: KeptCompanyView) {
    this._mode = v.mode;
    this._search = v.search;
    this._items = v.items;
    this._error = v.error;
    this._notice = v.notice;
    this._detail = v.detail;
    this._draft = v.draft;
    this._dirty = v.dirty;
    this._tab = v.tab;
    this._tabError = v.tabError;
    this._loadedTabs = v.loadedTabs;
    this._accounts = v.accounts;
    this._accountsDetail = v.accountsDetail;
    this._supply = v.supply;
    this._history = v.history;
    this._invoices = v.invoices;
    this._itemsList = v.itemsList;
    this._summary = v.summary;
    this._staleReloadId = v.staleReloadId;
    // No request is in flight for a kept screen — keep spinners at rest.
    this._loading = false;
    this._saving = false;
    this._tabLoading = false;
  }

  private _resetView() {
    this._reset();
    this._search = '';
    this._loading = false;
    this._saving = false;
    this._tabLoading = false;
    this._tabError = '';
  }

  // ── List ──────────────────────────────────────────────────────────────

  private _reset() {
    this._mode = 'list';
    this._items = [];
    this._detail = null;
    this._draft = null;
    this._dirty = false;
    this._error = '';
    this._notice = '';
    this._tab = 'info';
    this._loadedTabs = {};
    this._accounts = [];
    this._accountsDetail = [];
    this._supply = [];
    this._history = [];
    this._invoices = [];
    this._itemsList = [];
    this._summary = [];
    this._staleReloadId = null;
  }

  // ── Live server events (auto-refresh) ──────────────────────────────

  /** Does this event type concern the entity kind currently displayed? */
  private _eventConcernsPanel(typeName: string): boolean {
    switch (this.companyType) {
      case 'opscustomer': return typeName === 'customer';
      case 'opscompany': return typeName === 'company';
      case 'opssalesrep': return typeName === 'salesrep';
      case 'opspromoter': return typeName === 'promoter';
      default: return false;
    }
  }

  /** Mirrors the desktop behavior: refresh only when the event's record id
   *  matches the open detail; otherwise refresh the list. */
  private _onServerEvent = (ev: ServerEvent) => {
    if (ev.store !== this.storeCode || !this._eventConcernsPanel(ev.typeName)) return;
    const id = ev.value;
    const detailMatch = this._mode === 'detail' && !!this._detail && String(this._detail.id) === id;

    if (detailMatch) {
      if (ev.code === 'deleted') {
        this._mode = 'list';
        this._detail = null;
        this._draft = null;
        this._dirty = false;
        this._staleReloadId = null;
        this._notice = `This ${this._meta.singular.toLowerCase()} was deleted on the server.`;
        this._reloadListSoon();
        return;
      }
      if (this._dirty) {
        this._staleReloadId = id;
        return;
      }
      this._reloadDetailSoon(id);
      return;
    }

    if (this._mode === 'list') this._reloadListSoon();
  };

  /** Connection (re)established / ppServer reconnected — refresh visible view. */
  private _onServerLifecycle = (ev: { event: string }) => {
    if (ev.event !== 'resync') return;
    if (this._mode === 'detail' && this._detail && !this._dirty) {
      this._reloadDetailSoon(String(this._detail.id));
    } else if (this._mode === 'list') {
      this._reloadListSoon();
    }
  };

  private _reloadStaleRecord() {
    const id = this._staleReloadId;
    this._staleReloadId = null;
    if (id) void this._openDetail(id);
  }

  /** Quiet refresh of the open detail: keeps the active tab, re-fetches its data. */
  private async _refreshDetailQuiet(id: string) {
    if (!this.isConnected || this._dirty || this._saving) return;
    if (this._mode !== 'detail' || String(this._detail?.id ?? '') !== id) return;
    const epoch = this._view.epoch;
    try {
      const res = await api.staffCompanyDetail(this.storeCode, this.companyType, id);
      if (!this._view.isCurrent(epoch)) return;
      if (!res.data || this._dirty || this._saving) return;
      const tab = this._tab;
      this._detail = res.data;
      this._draft = { ...res.data };
      this._loadedTabs = {};
      this._accounts = [];
      this._accountsDetail = [];
      this._supply = [];
      this._history = [];
      this._invoices = [];
      this._itemsList = [];
      this._summary = [];
      this._tabError = '';
      if (tab !== 'info') this._switchTab(tab);
    } catch {
      // Transient — the next event / reconnect resync will retry.
    }
  }

  private async _loadList() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    try {
      const res = await api.staffCompanies(this.storeCode, this.companyType, this._search);
      if (!this._view.isCurrent(epoch)) return;
      this._items = res.success ? res.data : [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Failed to load list';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private async _openDetail(id: string) {
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    this._notice = '';
    try {
      const res = await api.staffCompanyDetail(this.storeCode, this.companyType, id);
      if (!this._view.isCurrent(epoch)) return;
      this._detail = res.data;
      this._draft = { ...res.data };
      this._dirty = false;
      this._mode = 'detail';
      this._tab = 'info';
      this._loadedTabs = {};
      this._accounts = [];
      this._accountsDetail = [];
      this._supply = [];
      this._history = [];
      this._invoices = [];
      this._itemsList = [];
      this._summary = [];
      this._tabError = '';
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Failed to load record';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private _startCreate() {
    this._detail = null;
    this._draft = {
      code: '', name: '', ic: '', email: '', telNo: '', faxNo: '', contact: '',
      address: '', postcode: '', state: '', country: '', city: '', idType: '',
      notes: '', tin: '',
      discount: 0, creditTerm: 0, debitTerm: 0, creditLimit: 0,
      purposeOfSales: '', isTaxable: false, canSellPoison: true,
    };
    this._dirty = false;
    this._mode = 'create';
    this._tab = 'info';
    this._error = '';
    this._notice = '';
  }

  private async _save() {
    if (!this._draft || !this.isAdmin) return;
    this._saving = true;
    this._error = '';
    this._notice = '';
    const epoch = this._view.epoch;
    try {
      if (this._mode === 'create') {
        const res = await api.staffCompanyCreate(this.storeCode, this.companyType, this._draft);
        if (!this._view.isCurrent(epoch)) return;
        this._detail = res.data;
        this._draft = { ...res.data };
        this._mode = 'detail';
        this._notice = `${this._meta.singular} created.`;
      } else if (this._detail) {
        const res = await api.staffCompanyUpdate(this.storeCode, this.companyType, this._detail.id, this._draft);
        if (!this._view.isCurrent(epoch)) return;
        this._detail = res.data;
        this._draft = { ...res.data };
        this._notice = 'Saved.';
      }
      this._dirty = false;
      void this._loadList();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Save failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._saving = false;
    }
  }

  private async _delete() {
    if (!this._detail || !this.isAdmin) return;
    if (!window.confirm(`Delete ${this._meta.singular.toLowerCase()} ${this._detail.name || this._detail.code}?`)) return;
    const epoch = this._view.epoch;
    this._error = '';
    try {
      await api.staffCompanyDelete(this.storeCode, this.companyType, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._reset();
      void this._loadList();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Delete failed';
    }
  }

  private _backToList() {
    if (this._dirty && !window.confirm('Discard unsaved changes?')) return;
    this._reset();
    void this._loadList();
  }

  // ── Tabs ──────────────────────────────────────────────────────────────

  private _switchTab(t: Tab) {
    this._tab = t;
    this._tabError = '';
    if (this._mode === 'create' || !this._detail || this._loadedTabs[t]) return;
    this._loadedTabs = { ...this._loadedTabs, [t]: true };
    switch (t) {
      case 'accounts': void this._loadAccounts(false); break;
      case 'account-detail': void this._loadAccounts(true); break;
      case 'supply': void this._loadSupply(); break;
      case 'history': void this._loadHistory(); break;
      case 'invoices': void this._loadInvoices(); break;
      case 'items': void this._loadItems(); break;
      case 'summary': void this._loadSummary(); break;
      default: break;
    }
  }

  private async _loadAccounts(detail: boolean) {
    if (!this._detail) return;
    const epoch = this._view.epoch;
    this._tabLoading = true;
    try {
      const res = await api.staffCompanyAccount(this.storeCode, this._detail.id, detail);
      if (!this._view.isCurrent(epoch)) return;
      if (detail) this._accountsDetail = res.data || [];
      else this._accounts = res.data || [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._tabError = err instanceof Error ? err.message : 'Failed to load accounts';
    } finally {
      if (this._view.isCurrent(epoch)) this._tabLoading = false;
    }
  }

  private async _loadSupply() {
    if (!this._detail) return;
    const epoch = this._view.epoch;
    this._tabLoading = true;
    try {
      const res = await api.staffCompanySupplyList(this.storeCode, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._supply = res.data || [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._tabError = err instanceof Error ? err.message : 'Failed to load supply list';
    } finally {
      if (this._view.isCurrent(epoch)) this._tabLoading = false;
    }
  }

  private async _loadHistory() {
    if (!this._detail) return;
    const epoch = this._view.epoch;
    this._tabLoading = true;
    try {
      const res = await api.staffCompanyHistory(this.storeCode, this.companyType, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._history = res.data || [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._tabError = err instanceof Error ? err.message : 'Failed to load history';
    } finally {
      if (this._view.isCurrent(epoch)) this._tabLoading = false;
    }
  }

  private async _loadInvoices() {
    if (!this._detail) return;
    const epoch = this._view.epoch;
    this._tabLoading = true;
    try {
      const res = await api.staffCompanyInvoices(this.storeCode, this.companyType, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._invoices = res.data || [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._tabError = err instanceof Error ? err.message : 'Failed to load invoices';
    } finally {
      if (this._view.isCurrent(epoch)) this._tabLoading = false;
    }
  }

  private async _loadItems() {
    if (!this._detail) return;
    const epoch = this._view.epoch;
    this._tabLoading = true;
    try {
      const res = await api.staffCompanyItems(this.storeCode, this.companyType, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._itemsList = res.data || [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._tabError = err instanceof Error ? err.message : 'Failed to load item list';
    } finally {
      if (this._view.isCurrent(epoch)) this._tabLoading = false;
    }
  }

  private async _loadSummary() {
    if (!this._detail) return;
    const epoch = this._view.epoch;
    this._tabLoading = true;
    try {
      const res = await api.staffCompanySummary(this.storeCode, this.companyType, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._summary = res.data || [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._tabError = err instanceof Error ? err.message : 'Failed to load transaction summary';
    } finally {
      if (this._view.isCurrent(epoch)) this._tabLoading = false;
    }
  }

  // ── Field helpers ─────────────────────────────────────────────────────

  private _setField(key: string, value: unknown) {
    if (!this._draft) return;
    (this._draft as Record<string, unknown>)[key] = value;
    this._dirty = true;
  }

  private _text(label: string, key: string, value: unknown, type = 'text') {
    const v = value === undefined || value === null ? '' : String(value);
    if (!this.isAdmin) {
      return html`<div class="fg"><label>${label}</label><div class="ro">${v || '—'}</div></div>`;
    }
    return html`
      <div class="fg">
        <label>${label}</label>
        <input type=${type} .value=${v}
          @input=${(e: InputEvent) => this._setField(key, (e.target as HTMLInputElement).value)} />
      </div>`;
  }

  private _number(label: string, key: string, value: unknown) {
    const num = typeof value === 'number' ? value : 0;
    if (!this.isAdmin) {
      return html`<div class="fg"><label>${label}</label><div class="ro">${num || '—'}</div></div>`;
    }
    return html`
      <div class="fg">
        <label>${label}</label>
        <input type="number" step="any" .value=${String(num)}
          @input=${(e: InputEvent) =>
            this._setField(key, parseFloat((e.target as HTMLInputElement).value) || 0)} />
      </div>`;
  }

  private _bool(label: string, key: string, value: unknown) {
    const on = value === true;
    if (!this.isAdmin) {
      return html`<div class="fg"><label>${label}</label><div class="ro">${on ? 'Yes' : 'No'}</div></div>`;
    }
    return html`
      <div class="fg">
        <label>${label}</label>
        <div class="ro">
          <input type="checkbox" style="width:auto" .checked=${on}
            @change=${(e: Event) => this._setField(key, (e.target as HTMLInputElement).checked)} />
        </div>
      </div>`;
  }

  private _area(label: string, key: string, value: unknown, full = false) {
    const v = value === undefined || value === null ? '' : String(value);
    if (!this.isAdmin) {
      return html`<div class="fg ${full ? 'full' : ''}"><label>${label}</label><div class="ro">${v || '—'}</div></div>`;
    }
    return html`
      <div class="fg ${full ? 'full' : ''}">
        <label>${label}</label>
        <textarea .value=${v}
          @input=${(e: InputEvent) => this._setField(key, (e.target as HTMLTextAreaElement).value)}></textarea>
      </div>`;
  }

  private _dobField() {
    const sec = this._draft?.dob || 0;
    const v = sec > 0 ? new Date(sec * 1000).toISOString().slice(0, 10) : '';
    if (!this.isAdmin) {
      return html`<div class="fg"><label>Date of Birth</label><div class="ro">${v || '—'}</div></div>`;
    }
    return html`
      <div class="fg">
        <label>Date of Birth</label>
        <input type="date" .value=${v}
          @input=${(e: InputEvent) => {
            const val = (e.target as HTMLInputElement).value;
            this._setField('dob', val ? Math.floor(new Date(val).getTime() / 1000) : 0);
          }} />
      </div>`;
  }

  private _fmtDate(sec?: number) {
    if (!sec) return '—';
    return new Date(sec * 1000).toISOString().slice(0, 10);
  }

  /** SQL rows may carry epoch ms (desktop stores ms) or seconds. */
  private _fmtAnyDate(v?: number) {
    if (!v) return '—';
    const sec = v > 1e12 ? Math.floor(v / 1000) : v;
    return new Date(sec * 1000).toISOString().slice(0, 10);
  }

  private _fmtMoney(n?: number) {
    return n === undefined || n === null ? '—' : `RM ${Number(n).toFixed(2)}`;
  }

  /** Desktop String::FormatSpecial — fixed nDec decimals, all-zero fraction trimmed ("2.00" → "2"). */
  private _fmtSpecial(v: number | undefined, nDec: number) {
    if (v === undefined || v === null || Number.isNaN(v)) return '0';
    const s = Number(v).toFixed(nDec);
    const frac = s.split('.')[1] ?? '';
    if (frac.length > 0 && frac.split('').every((c) => c === '0')) return s.slice(0, s.length - nDec - 1);
    return s;
  }

  /** Desktop String::FormatNumber — fixed 2 decimals with thousands separators. */
  private _fmtNum(v?: number) {
    if (v === undefined || v === null || Number.isNaN(v)) return '0.00';
    return Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private _fmtDateTime(v?: number) {
    if (!v) return '—';
    const sec = v > 1e12 ? Math.floor(v / 1000) : v;
    const d = new Date(sec * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  // ── Render ────────────────────────────────────────────────────────────

  render() {
    if (!this.storeCode) return html`<p style="padding:1rem">Select an outlet first.</p>`;
    return html`
      ${this._error ? html`<p class="err">${this._error}</p>` : ''}
      ${this._notice ? html`<p style="color:var(--color-success,#188038);font-size:0.8rem;margin:0.25rem 0">${this._notice}</p>` : ''}
      ${this._staleReloadId
        ? html`<p style="color:var(--color-warning,#b06000);font-size:0.8rem;margin:0.25rem 0;display:flex;align-items:center;gap:0.5rem">
            This record was updated on the server — your unsaved edits may be out of date.
            <button class="btn-sm" @click=${this._reloadStaleRecord}>Reload</button>
          </p>`
        : ''}
      ${this._mode === 'list' ? this._renderList() : this._renderDetail()}
    `;
  }

  private _renderList() {
    return html`
      <div class="tab-body">
        <div class="list-tools">
          <input placeholder="Search ${this._meta.title.toLowerCase()} by name or code…" .value=${this._search}
            @input=${(e: InputEvent) => this._search = (e.target as HTMLInputElement).value}
            @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._loadList()} />
          <button class="btn" @click=${this._loadList}>Search</button>
          ${this.isAdmin ? html`<button class="btn btn-sec" @click=${this._startCreate}>＋ New ${this._meta.singular}</button>` : ''}
        </div>
        ${this._loading ? html`<div class="loading">Loading…</div>` : ''}
        ${!this._loading && this._items.length === 0 ? html`<div class="empty">No ${this._meta.title.toLowerCase()} found.</div>` : ''}
        ${this._items.length > 0 ? html`
          <div class="table-wrap">
            <table>
              <thead>
                <tr><th>Description</th><th></th></tr>
              </thead>
              <tbody>
                ${this._items.map((r) => html`
                  <tr class="clickable" @click=${() => this._openDetail(r.id)}>
                    <td>${r.description || '—'}</td>
                    <td>
                      <button class="btn btn-sm"
                        @click=${(e: Event) => { e.stopPropagation(); this._openDetail(r.id); }}>
                        ${this.isAdmin ? '✏️ Edit' : '👁 View'}
                      </button>
                    </td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        ` : ''}
      </div>
    `;
  }

  private _renderDetail() {
    const d = this._draft;
    if (!d) return html`<div class="loading">Loading…</div>`;
    const tabs = this._mode === 'create' ? this._tabs.filter((t) => t.id === 'info') : this._tabs;

    return html`
      <div class="detail-head">
        <button class="btn btn-sec btn-sm" @click=${this._backToList}>← Back</button>
        <h3>${this._mode === 'create' ? `New ${this._meta.singular}` : `${this._detail?.code || ''} — ${this._detail?.name || ''}`}</h3>
        <span class="badge">${this._meta.icon} ${this._meta.singular}</span>
        <div class="spacer"></div>
        ${this.isAdmin && this._mode === 'detail' ? html`
          <button class="btn btn-danger btn-sm" @click=${this._delete}>Delete</button>
        ` : ''}
        ${this.isAdmin ? html`
          <button class="btn btn-sec btn-sm" ?disabled=${!this._dirty} @click=${() => { this._draft = { ...(this._detail || {}) } as Partial<CompanyDetail>; this._dirty = false; }}>Discard</button>
          <button class="btn btn-sm" ?disabled=${this._saving || (!this._dirty && this._mode === 'detail')} @click=${this._save}>
            ${this._saving ? 'Saving…' : this._mode === 'create' ? 'Create' : 'Save'}
          </button>
        ` : ''}
      </div>

      <div class="tabs">
        ${tabs.map((t) => html`
          <button class=${this._tab === t.id ? 'active' : ''} @click=${() => this._switchTab(t.id)}>${t.label}</button>
        `)}
      </div>

      <div class="tab-body">
        ${this._tab === 'info' ? this._renderInfo() : ''}
        ${this._tab === 'accounts' ? this._renderAccounts(this._accounts, 'No account transactions.') : ''}
        ${this._tab === 'account-detail' ? this._renderAccounts(this._accountsDetail, 'No account transactions.') : ''}
        ${this._tab === 'supply' ? this._renderSupply() : ''}
        ${this._tab === 'history' ? this._renderHistory() : ''}
        ${this._tab === 'invoices' ? this._renderInvoices() : ''}
        ${this._tab === 'items' ? this._renderItems() : ''}
        ${this._tab === 'summary' ? this._renderSummary() : ''}
      </div>
    `;
  }

  private _renderInfo() {
    const d = this._draft!;
    return html`
      <div class="grid">
        ${this._text('Code', 'code', d.code)}
        ${this._text('Name', 'name', d.name)}
        ${this._text('Reg / IC No', 'ic', d.ic)}
        ${this._text('Email', 'email', d.email, 'email')}
        ${this._text('Tel No', 'telNo', d.telNo)}
        ${this._text('Fax No', 'faxNo', d.faxNo)}
        ${this._text('Contact Person', 'contact', d.contact)}
        ${this._text('Postcode', 'postcode', d.postcode)}
        ${this._text('City', 'city', d.city)}
        ${this._text('State', 'state', d.state)}
        ${this._text('Country', 'country', d.country)}
        ${this._text('ID Type', 'idType', d.idType)}
        ${this._text('TIN', 'tin', d.tin)}
        ${this._dobField()}
        ${this._number('Discount %', 'discount', d.discount)}
        ${this._number('Credit Term', 'creditTerm', d.creditTerm)}
        ${this._number('Debit Term', 'debitTerm', d.debitTerm)}
        ${this._number('Credit Limit', 'creditLimit', d.creditLimit)}
        ${this._text('Purpose of Sales', 'purposeOfSales', d.purposeOfSales)}
        ${this._bool('Taxable', 'isTaxable', d.isTaxable)}
        ${this._bool('Can Receive Poison', 'canSellPoison', d.canSellPoison)}
        ${this._area('Address', 'address', d.address, true)}
        ${this._area('Notes', 'notes', d.notes, true)}
        ${this._mode === 'detail' ? html`
          <div class="fg"><label>Gender</label><div class="ro">${d.gender || '—'}</div></div>
          <div class="fg"><label>Race</label><div class="ro">${d.race || '—'}</div></div>
          <div class="fg"><label>Marital Status</label><div class="ro">${d.maritalStatus || '—'}</div></div>
        ` : ''}
      </div>
    `;
  }

  private _renderAccounts(rows: AccountRow[], emptyMsg: string) {
    if (this._tabLoading) return html`<div class="loading">Loading…</div>`;
    if (this._tabError) return html`<p class="err">${this._tabError}</p>`;
    if (rows.length === 0) return html`<div class="empty">${emptyMsg}</div>`;
    return html`
      <div class="table-wrap">
        <table>
          <thead><tr><th>Date</th><th>Ref</th><th class="num">Debit</th><th class="num">Credit</th><th>Remark</th></tr></thead>
          <tbody>
            ${rows.map((r) => html`
              <tr>
                <td>${this._fmtDate(r.date)}</td>
                <td>${r.refNo || '—'}</td>
                <td class="num debit">${r.debit ? this._fmtMoney(r.debit) : '—'}</td>
                <td class="num">${r.credit ? this._fmtMoney(r.credit) : '—'}</td>
                <td class="wrap">${r.remark || '—'}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  private _renderSupply() {
    if (this._tabLoading) return html`<div class="loading">Loading…</div>`;
    if (this._tabError) return html`<p class="err">${this._tabError}</p>`;
    if (this._supply.length === 0) return html`<div class="empty">No items in the supply list.</div>`;
    // Desktop parity (CompanyEditPanel_Supply.cpp): Item | Price | QOH | Reorder Qty | Scheme.
    return html`
      <div class="table-wrap">
        <table>
          <thead><tr><th>Item</th><th class="num">Price</th><th class="num">QOH</th><th class="num">Reorder Qty</th><th>Scheme</th></tr></thead>
          <tbody>
            ${this._supply.map((r) => html`
              <tr>
                <td>${r.name || '—'}</td>
                <td class="num">${this._fmtNum(r.costPrice)}</td>
                <td class="num">${r.qohString || '—'}</td>
                <td class="num">${r.reorderQty ?? '—'}</td>
                <td>${r.bonusScheme || '—'}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  private _renderHistory() {
    if (this._tabLoading) return html`<div class="loading">Loading…</div>`;
    if (this._tabError) return html`<p class="err">${this._tabError}</p>`;
    if (this._history.length === 0) return html`<div class="empty">No history.</div>`;
    const supplier = this._meta.supplier;
    // Desktop parity: Purchase History (supplier windows) and Sales History
    // (customer / sales rep / promoter windows) use different column sets.
    // Quantity/Bonus follow String::FormatSpecial (1 dec for purchases, 2 for sales) + pack code.
    const qty = (v?: number, pack?: string) => this._fmtSpecial(v, supplier ? 1 : 2) + (pack || '');
    return html`
      <div class="table-wrap">
        <table>
          ${supplier ? html`
            <thead><tr>
              <th>Date</th><th>#</th><th>Ref</th><th>Code</th><th>Name</th><th class="num">Quantity</th><th class="num">Bonus</th>
              <th class="num">Price</th><th class="num">Net Price</th><th class="num">QOH</th><th>Brand</th><th>Generic Name</th>
              <th>Batch NO</th><th>Expiry Date</th><th>Remark</th>
            </tr></thead>
            <tbody>
              ${this._history.map((r) => html`
                <tr>
                  <td>${this._fmtDate(r.date)}</td>
                  <td>${r.transactionId || '—'}</td>
                  <td>${r.refNo || '—'}</td>
                  <td>${r.itemCode || '—'}</td>
                  <td>${r.item || '—'}</td>
                  <td class="num">${qty(r.qty, r.pack)}</td>
                  <td class="num">${qty(r.bonusQty, r.bonusPack)}</td>
                  <td class="num">${this._fmtNum(r.price)}</td>
                  <td class="num">${this._fmtNum(r.netPrice)}</td>
                  <td class="num">${r.qohString || '—'}</td>
                  <td>${r.brand || '—'}</td>
                  <td>${r.genericName || '—'}</td>
                  <td>${r.batchNo || '—'}</td>
                  <td>${this._fmtDate(r.expiryDate)}</td>
                  <td class="wrap">${r.remark || '—'}</td>
                </tr>
              `)}
            </tbody>
          ` : html`
            <thead><tr>
              <th>Date</th><th>#</th><th>Code</th><th>Name</th><th class="num">Quantity</th><th class="num">Bonus</th><th class="num">Price</th>
              <th>Brand</th><th>Generic Name</th><th class="num">Discount</th><th>Batch No</th><th>Expiry Date</th>
              <th>Location</th><th>Staff</th><th>Time</th>
            </tr></thead>
            <tbody>
              ${this._history.map((r) => html`
                <tr>
                  <td>${this._fmtDate(r.date)}</td>
                  <td>${r.refNo || '—'}</td>
                  <td>${r.itemCode || '—'}</td>
                  <td>${r.item || '—'}</td>
                  <td class="num">${qty(r.qty, r.pack)}</td>
                  <td class="num">${qty(r.bonusQty, r.bonusPack)}</td>
                  <td class="num">${this._fmtNum(r.price)}</td>
                  <td>${r.brand || '—'}</td>
                  <td>${r.genericName || '—'}</td>
                  <td class="num">${this._fmtNum(r.discount)}</td>
                  <td>${r.batchNo || '—'}</td>
                  <td>${this._fmtDate(r.expiryDate)}</td>
                  <td>${r.location || '—'}</td>
                  <td>${r.who || '—'}</td>
                  <td>${this._fmtDateTime(r.dateCreated)}</td>
                </tr>
              `)}
            </tbody>
          `}
        </table>
      </div>
    `;
  }

  private _renderInvoices() {
    if (this._tabLoading) return html`<div class="loading">Loading…</div>`;
    if (this._tabError) return html`<p class="err">${this._tabError}</p>`;
    if (this._invoices.length === 0) return html`<div class="empty">No invoices.</div>`;
    return html`
      <div class="table-wrap">
        <table>
          <thead>
            <tr><th>Date</th><th>#</th><th>Ref</th><th>Term</th><th class="num">Total</th><th>Paid On</th><th>Paid By</th><th>Pay Ref</th><th class="num">Total Paid</th><th class="num">Balance</th></tr>
          </thead>
          <tbody>
            ${this._invoices.map((r) => html`
              <tr>
                <td>${this._fmtAnyDate(r.date)}</td>
                <td>${r.id || '—'}</td>
                <td>${r.refNo || '—'}</td>
                <td>${r.term || '—'}</td>
                <td class="num">${this._fmtMoney(r.total)}</td>
                <td>${r.paidOn || '—'}</td>
                <td>${r.paidBy || '—'}</td>
                <td>${r.payRef || '—'}</td>
                <td class="num">${this._fmtMoney(r.totalPaid)}</td>
                <td class="num debit">${this._fmtMoney((r.total || 0) - (r.totalPaid || 0))}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  private _renderItems() {
    if (this._tabLoading) return html`<div class="loading">Loading…</div>`;
    if (this._tabError) return html`<p class="err">${this._tabError}</p>`;
    if (this._itemsList.length === 0) return html`<div class="empty">No items.</div>`;
    // Desktop parity (CompanyEditPanel_Quotation.cpp): Code | Name | QOH | Price |
    // Quantity(+pack) | Bonus(+bonusPack) | Date | SalesRep/Promoter | Remark.
    return html`
      <div class="table-wrap">
        <table>
          <thead><tr><th>Code</th><th>Name</th><th class="num">QOH</th><th class="num">Price</th><th class="num">Quantity</th><th class="num">Bonus</th><th>Date</th><th>${this._meta.supplier ? 'SalesRep' : 'Promoter'}</th><th>Remark</th></tr></thead>
          <tbody>
            ${this._itemsList.map((r) => html`
              <tr>
                <td>${r.itemCode || '—'}</td>
                <td>${r.item || '—'}</td>
                <td class="num">${r.qohString || '—'}</td>
                <td class="num">${this._fmtNum(r.price)}</td>
                <td class="num">${this._fmtSpecial(r.qty, 2) + (r.pack || '')}</td>
                <td class="num">${this._fmtSpecial(r.bonusQty, 2) + (r.bonusPack || '')}</td>
                <td>${this._fmtDate(r.date)}</td>
                <td>${r.salesRep || '—'}</td>
                <td class="wrap">${r.remark || '—'}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  private _renderSummary() {
    if (this._tabLoading) return html`<div class="loading">Loading…</div>`;
    if (this._tabError) return html`<p class="err">${this._tabError}</p>`;
    if (this._summary.length === 0) return html`<div class="empty">No transactions in the last 12 months.</div>`;
    return html`
      <div class="table-wrap">
        <table>
          <thead><tr><th>Item</th><th>Month</th><th class="num">Quantity</th></tr></thead>
          <tbody>
            ${this._summary.map((r) => html`
              <tr>
                <td>${r.item || '—'}</td>
                <td>${r.month || '—'}</td>
                <td class="num">${r.qty ?? '—'}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }
}
